import { installGlobalHandlers, sendBeaconHeartbeat } from "@/lib/clientLogger";
import { ClientFactory } from "@/main/common/ClientFactory";
import { installUiInteractionCensus } from "@/main/analytics/uiInteractionCensus";
import { installDomReconciliationGuards } from "@/lib/domReconciliationGuard";
import { installArrayPolyfills } from "@/lib/arrayPolyfills";
import { installPromisePolyfills } from "@/lib/promisePolyfills";
// FI-3157 (r131): the install decision only; the scanner is a lazy chunk the gate fetches where it runs (never prod).
import { installLayoutHealthMonitorWhenEnabled } from "@/lib/layoutHealthMonitorGate";
import { initializeViewportRuntime, resetViewportRuntimeForTests } from "@/lib/viewportRuntime";
import { installUnusablePageSignals, resetUnusablePageSignalsForTests } from "@/lib/unusablePageSignals";
import { resetAppAuthHandoffRuntimeForTests, setupAppAuthHandoffRuntime } from "@/main/common/AppAuthHandoffRuntime";
import { resetNativeMobileAuthReturnForTests, setupNativeMobileAuthReturn } from "@/main/mobile-shell/MobileAuthBridge";
// Type-only: both bridges are lazy chunks loaded inside the native shell alone (FI-2514, `setupNativeLifecycleBridges`).
import type * as MobileDeepLinkBridgeModule from "@/main/mobile-shell/MobileDeepLinkBridge";
import type * as MobileResumeStateBridgeModule from "@/main/mobile-shell/MobileResumeStateBridge";
// Type-only as well (FI-3157, r131): the native UX module is a lazy chunk loaded inside the native shell alone.
import type * as MobileShellNativeUxModule from "@/main/mobile-shell/MobileShellNativeUx";
// Type-only too (r198aa, `designs/01-now/2026-08-01-header-startup-cost-design.md` §11): the share-target bridge and the
// composer seed it writes are a lazy chunk loaded inside the native shell alone.
import type * as MobileShareTargetBridgeModule from "@/main/mobile-shell/MobileShareTargetBridge";
import { resetMobilePushBridgeForTests, setupMobilePushBridge } from "@/main/mobile-shell/MobilePushBridge";
import {
    resetNativeAppIdentityForTests,
    setupNativeAppIdentity,
} from "@/main/mobile-shell/MobileAppIdentityBridge";
import { syncNativeAppLanguage } from "@/main/mobile-shell/MobileAppLanguageBridge";
import { MobileShellRuntime } from "@/main/mobile-shell/MobileShellRuntime";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import { resetAuthSessionSyncForTests, setupAuthSessionSync } from "@/main/common/AuthSessionSync";
import { resetProactiveCsrfRefreshForTests, setupProactiveCsrfRefresh } from "@/main/common/CsrfProactiveRefresh";
import { resetFirstTouchBeaconForTests, sendFirstTouchBeacon } from "@/main/common/FirstTouchBeacon";
import {
    persistInferredContentLocale,
    resetInferredContentLocaleCookieForTests,
} from "@/main/common/InferredContentLocaleCookie";
import {
    initializeHeaderInboxFromDocument,
    refreshHeaderInbox,
    resetHeaderInboxForTests,
    setupHeaderInboxAutoRefresh,
} from "@/main/headerInbox/HeaderInboxStore";
import { resetEnhancedNavigationForTests, setupEnhancedNavigation } from "@/main/navigation/EnhancedNavigation";
import { beginPageInstance } from "@/main/navigation/CurrentPageInstance";
import { resetNavigationPrefetchForTests, setupNavigationPrefetch } from "@/main/navigation/NavigationPrefetch";
import { resetInPageAnchorScrollForTests, setupInPageAnchorScroll } from "@/main/navigation/InPageAnchorScroll";
import { installNavigationStateTracking, resetNavigationStateForTests } from "@/main/navigation/NavigationState";
import {
    applyPostMountDocumentEffectsFromDocument,
    applyPreMountDocumentEffectsFromDocument,
} from "@/main/navigation/NavigationDocumentEffects";
import { initializeThemeRuntime, resetThemeRuntimeForTests } from "@/main/navigation/ThemeRuntime";
import { resetPwaInstallRuntimeForTests, setupPwaInstallRuntime } from "@/main/pwa/PwaInstallRuntime";
import { initializeShellStateFromDocument } from "@/main/navigation/ShellStateStore";
import { initializeCookieConsent, resetCookieConsentForTests } from "@/main/analytics/consent/cookieConsent";
import { resetWebVitalsReportingForTests, setupWebVitalsReporting } from "@/main/analytics/webVitals";
import { mountAll } from "@/main/react/ReactMounter";
import { maybeTriggerOnboardingWelcome, resetOnboardingWelcomeForTests } from "@/main/onboarding/OnboardingWelcomeRuntime";
import { maybeScheduleFeedbackPrompt } from "@/main/feedback/FeedbackPromptRuntime";
import { setupUnreadDocumentBadge } from "@/main/navigation/DocumentTitleBadge";

let globalFrontEndSetupComplete = false;

/** Read by the `touch-only` variant in `tailwind.css` (chat completeness §F31): present only inside the app shell. */
export const NATIVE_SHELL_DOCUMENT_ATTRIBUTE = "data-native-shell";

/**
 * How long after boot the install-attribution report waits: past the startup burst, so it never competes
 * with the first render or with `setupNativeAppIdentity`, whose version label it reads.
 */
const INSTALL_ATTRIBUTION_DELAY_MS = 3000;
let installAttributionScheduled = false;

/**
 * Where Play says this install came from (Play Install Referrer), reported once signed out and once to link
 * it at sign-in. FI-719: the bridge is imported only here, AFTER the delay, and only inside the native
 * shell, so its 3.7 KB left the eager startup graph that every web visitor parses. The bridge itself stays
 * inert on iOS and on any APK without the plugin, exactly as before. A chunk that cannot load reports
 * nothing and the next launch retries, the same outcome as a failed post.
 */
function scheduleInstallAttribution(): void {
    if (installAttributionScheduled || !MobileShellRuntime.isNativeShell()) {
        return;
    }
    installAttributionScheduled = true;
    window.setTimeout(() => {
        // The `{ default: m }` wrap is the idiom for a module of named exports (the helper is typed for default).
        void importChunkWithRetryAndReload(
            () => import("@/main/mobile-shell/MobileInstallAttributionBridge").then((m) => ({ default: m })),
            "install-attribution",
        )
            .then(({ default: bridge }) => bridge.reportInstallAttribution())
            .catch(() => undefined);
    }, INSTALL_ATTRIBUTION_DELAY_MS);
}

let nativeLifecycleBridgesScheduled = false;
let loadedNativeUx: typeof MobileShellNativeUxModule | null = null;
let loadedDeepLinkBridge: typeof MobileDeepLinkBridgeModule | null = null;
let loadedResumeStateBridge: typeof MobileResumeStateBridgeModule | null = null;
let loadedShareTargetBridge: typeof MobileShareTargetBridgeModule | null = null;

/**
 * Content shared INTO the app from Android's share sheet (r198aa): a lazy chunk, native shell only, like the bridges
 * above. Nothing is lost by the chunk round trip: the setup's own `consumePendingShare()` poll reads the payload the
 * plugin holds until read, the same poll that serves a cold start. A chunk that cannot load leaves the intake off for
 * this document, the same outcome as an APK without the plugin.
 */
function setupNativeShareTargetLazily(): void {
    if (!MobileShellRuntime.isNativeShell()) {
        return;
    }
    // The `{ default: m }` wrap is the idiom for a module of named exports (the helper is typed for default).
    void importChunkWithRetryAndReload(
        () => import("@/main/mobile-shell/MobileShareTargetBridge").then((m) => ({ default: m })),
        "native-share-target",
    )
        .then(({ default: bridge }) => {
            loadedShareTargetBridge = bridge;
            bridge.setupNativeShareTarget();
        })
        .catch(() => undefined);
}

/**
 * Content deep links (App Links) + the app-resume policy, then the route + scroll restored after Android killed the
 * backgrounded app (r106 D34). FI-2514 (r108 `1m`): both bridges are native-only and were ~10 KB of every web
 * visitor's eager startup graph, so they load here as lazy chunks, and only inside the native shell (the resume chunk
 * only on Android, the one platform its setup does anything on).
 *
 * Both fetches start now, in the same tick, so the restore waits on one chunk round trip and nothing else. The ORDER
 * is the old one: deep links first, then resume state, whose restore waits on the launch-URL read the deep-link setup
 * starts (a notification tap or deep link wins). A chunk that cannot load leaves that bridge off for this document, the
 * same outcome as an APK without the plugin; `appUrlOpen` is retained by the App plugin until a listener consumes it.
 *
 * FI-3157 (r131): the native UX polish (`MobileShellNativeUx`: splash hide, status bar, hardware back, offline strip) is
 * native-only too, so it is the third chunk fetched here, in the same tick. Its setup still runs FIRST: the deep links
 * wait on it, because the resume path re-probes connectivity through the listeners it installs. A native-UX chunk that
 * cannot load leaves that polish off for this document and the bridges still start.
 */
function setupNativeLifecycleBridges(): void {
    if (nativeLifecycleBridgesScheduled || !MobileShellRuntime.isNativeShell()) {
        return;
    }
    nativeLifecycleBridgesScheduled = true;
    // The `{ default: m }` wrap is the idiom for a module of named exports (the helper is typed for default).
    const nativeUxReady: Promise<void> = importChunkWithRetryAndReload(
        () => import("@/main/mobile-shell/MobileShellNativeUx").then((m) => ({ default: m })),
        "native-shell-ux",
    )
        .then(({ default: nativeUx }) => {
            loadedNativeUx = nativeUx;
            nativeUx.setupMobileShellNativeUx();
        })
        .catch(() => undefined);
    const deepLinks = importChunkWithRetryAndReload(
        () => import("@/main/mobile-shell/MobileDeepLinkBridge").then((m) => ({ default: m })),
        "native-deep-links",
    );
    const resumeState: Promise<{ default: typeof MobileResumeStateBridgeModule } | null> = MobileShellRuntime.getPlatform() === "android"
        ? importChunkWithRetryAndReload(
            () => import("@/main/mobile-shell/MobileResumeStateBridge").then((m) => ({ default: m })),
            "native-resume-state",
        ).catch(() => null)
        : Promise.resolve(null);
    void nativeUxReady
        .then(() => deepLinks)
        .then(({ default: bridge }) => {
            loadedDeepLinkBridge = bridge;
            bridge.setupNativeDeepLinks();
            return resumeState;
        })
        .then((loaded: { default: typeof MobileResumeStateBridgeModule } | null) => {
            if (loaded) {
                loadedResumeStateBridge = loaded.default;
                loaded.default.setupNativeResumeState();
            }
        })
        .catch(() => undefined);
}

export function remountFrontEndSubtree(root: HTMLElement): Promise<void> {
    // The single mount chokepoint for a replaced navigation root — both soft paths (DocumentSwap and
    // mountClientOwnedRoute) come through here. Minting the page-instance id at this one place is what
    // makes history-entry ownership correct by construction: a mount path added later inherits it
    // without anyone remembering the design. Asserted by bootstrapFrontEnd.spec.ts so the invariant
    // survives a refactor of the callers.
    // designs/01-now/2026-07-25-history-entry-ownership-and-in-page-back-navigation-design.md (C3)
    beginPageInstance();
    // FI-3449: a `?lang=` soft navigation changes the in-app language without a reboot; deduped, so a no-op otherwise.
    syncNativeAppLanguage();

    // Page-level islands mount their chunk on demand (mountAll is async). Soft-nav callers may
    // ignore this promise — the swapped-in document is already on screen while the chunk loads.
    return mountAll(root);
}

export function bootstrapFrontEnd(): void {
    runOneTimeGlobalSetup();

    void finalizeMountFromCurrentDocument();
}

function runOneTimeGlobalSetup(): void {
    if (globalFrontEndSetupComplete) {
        return;
    }

    // Must run first, before mountAll: an old/embedded webview lacking Promise.allSettled would
    // otherwise throw synchronously inside mountAll()'s awaited settle and stall the whole cold boot
    // (splash never dismisses). No-op on modern engines.
    installPromisePolyfills();
    // FI-3894 (r157 A11): the app shell takes the `touch-only:` branch (tailwind.css) from this signal, not from the
    // pointer query its WebView does not match. Before any island mounts, so the first paint already has it.
    if (MobileShellRuntime.isNativeShell()) {
        document.documentElement.setAttribute(NATIVE_SHELL_DOCUMENT_ATTRIBUTE, "");
    }
    // Same slot, same reason: `@tiptap/core` calls Array.prototype.findLast (ES2023), which an old
    // Android System WebView lacks, crashing the DiscussionPage island (FI-160).
    installArrayPolyfills();

    // Must run before any React island mounts (mountAll below): hardens the DOM against the
    // third-party-mutation removeChild/insertBefore crash (Google Translate et al.) so an island
    // survives instead of white-screening through MountErrorBoundary.
    installDomReconciliationGuards();
    installGlobalHandlers();
    // Must follow installGlobalHandlers: the heartbeat proves THIS channel is alive, so sending it
    // before the channel is wired would prove nothing. W5's last half — see sendBeaconHeartbeat's
    // docstring for why a beacon with no heartbeat reads identically to a healthy app.
    sendBeaconHeartbeat();
    // Start fetching the generated API client now. Its classes left the eager startup graph on
    // 2026-09-16 (FA-1: `ClientFactory` resolves them from the lazy `generated-api-classes` chunk), and
    // starting the fetch at boot keeps the first interaction-driven request from waiting on a cold one.
    // A failure here is not swallowed for the real call: that call retries the load and rejects itself.
    void ClientFactory.loadGeneratedApiClasses().catch(() => undefined);
    // First-touch attribution (board round 93 D5): the anonymous discussion page no longer mints `sv_attrib`
    // server-side (it is edge-cacheable), so the landing is reported here, once per hard load. Before any island
    // mounts, so a URL-cleaning effect has not stripped the UTM tags yet. Inert for a signed-in viewer.
    sendFirstTouchBeacon();
    // FI-5280: the server's content-locale inference writes no cookie (it would veto the edge cache), so the inferred
    // chrome locale is persisted here, once per hard load, for the visitor's next request.
    persistInferredContentLocale();
    installLayoutHealthMonitorWhenEnabled();
    initializeViewportRuntime();
    // Must follow initializeViewportRuntime: this reads what that publishes, and both listen to the
    // same visualViewport events. Unlike installLayoutHealthMonitor above it stays ON in production —
    // it is event-driven and debounced, never polls, and never scans the DOM. Design:
    // designs/01-now/2026-08-16-unusable-page-observability-design.md
    installUnusablePageSignals();
    // The UI control census. Installed here, once, for the reason the module comment gives: DocumentSwap
    // replaces innerHTML, so the `document` node survives every soft navigation and one listener
    // outlives every island mount and JTE swap. Passive and capture-phase — it never calls
    // preventDefault/stopPropagation, so it cannot break a control, which is what makes installing one
    // across the whole product acceptable at all.
    // designs/01-now/2026-09-06-ui-control-census-design.md
    installUiInteractionCensus();
    // FI-3671: a back/forward cache restore shows the page as the member left it, so its badges are one forced
    // header-inbox summary refresh behind (the existing entry; the store bails out on an unchanged payload, rule 31).
    installNavigationStateTracking({
        onRestoredFromBackForwardCache: () => {
            void refreshHeaderInbox({ mode: "summary", force: true, reason: "bfcache-restore" });
        },
    });
    initializeThemeRuntime();
    // Catch the browser's install offer, which fires once and early and cannot be retrieved later.
    // It only STORES the event: it does not preventDefault, so the browser's own install affordance is
    // untouched, and nothing renders yet. Whoever ships the install entry point turns both on together
    // -- deferring the prompt while offering no replacement makes the product harder to install than
    // it was. See PwaInstallRuntime's module comment and
    // designs/05-needs-refresh/pwa-implementation-plan-2026-04-16.md (Phase 4 status, 2026-08-28).
    // Inert in the Capacitor shell, in an installed standalone window, and in every browser that does
    // not fire the event at all (iOS Safari, Firefox).
    setupPwaInstallRuntime();
    setupEnhancedNavigation();
    setupNavigationPrefetch();
    setupInPageAnchorScroll();
    setupAppAuthHandoffRuntime();
    setupNativeMobileAuthReturn();
    // The native UX polish (FI-3157), then content deep links (App Links) + the app-resume policy, then the route +
    // scroll restore after an Android kill (r106 D34): all lazy and native-only (FI-2514). The deep links follow the
    // native UX setup inside: the resume path re-probes connectivity through that module's installed listeners.
    setupNativeLifecycleBridges();
    // Content shared INTO the app from Android's share sheet. Inert outside the Capacitor shell and on
    // any APK built before the SeavienShareTarget plugin existed, which is what lets the web half
    // deploy ahead of the APK. A native-only lazy chunk (r198aa).
    setupNativeShareTargetLazily();
    // Native (FCM) push token listeners. Inert outside the Capacitor shell and while the platform's
    // readiness flag is off; never prompts for permission at startup.
    setupMobilePushBridge();
    // Read the installed binary's versionName/versionCode once, so the settings version row, the
    // client-error beacon and the push registration can each name the build they are talking about.
    // Fire-and-forget: every consumer already handles "no native identity", and nothing may wait on a
    // bridge call during startup. Inert on the web.
    void setupNativeAppIdentity();
    // FI-3449: tell the Android shell the in-app language, so its channel names and offline panel follow it, not the
    // phone's. Fire-and-forget; inert on the web, on iOS and on any APK without the plugin.
    syncNativeAppLanguage();
    // Where Play says this install came from (Play Install Referrer), reported once signed out and once to
    // link it at sign-in. Deferred and fire-and-forget; inert on the web and on any APK without the plugin.
    scheduleInstallAttribution();
    setupAuthSessionSync();
    setupProactiveCsrfRefresh();
    setupHeaderInboxAutoRefresh();
    // Puts the unread count in the tab title and, where the platform has one, on the app icon. Reads the
    // header inbox store and writes nothing back to it. Registered after the auto-refresh above so the
    // first poll it observes is a real one.
    setupUnreadDocumentBadge();
    // Field RUM: register Core Web Vitals observers once per session. Passive PerformanceObservers;
    // metrics only leave the device when consent has enabled GTM (enforced in pushAnalyticsEvent).
    // The library itself is a lazy chunk (FI-719); the promise never rejects and nothing waits on it.
    void setupWebVitalsReporting();
    globalFrontEndSetupComplete = true;
}

async function finalizeMountFromCurrentDocument(): Promise<void> {
    initializeShellStateFromDocument();
    initializeHeaderInboxFromDocument();
    applyPreMountDocumentEffectsFromDocument();

    // Path A (rendering-convergence plan Phase 1 — the tactical LCP win): if the server already
    // rendered real above-the-fold content (e.g. a non-deferred discussion's <h1> + body), lift the
    // splash NOW so that content becomes the Largest Contentful Paint, instead of waiting for the async
    // chunk download + mount. Because the route component renders that content from its props
    // immediately, the createRoot mount below rebuilds the SAME DOM in one synchronous commit — the
    // browser only paints after a commit, so there is no visible blank/flash frame to mask.
    //
    // Increment 2: on instant-content routes the inline splash script (splash-visual.jte) has usually
    // ALREADY dismissed the splash on first paint — before this bundle even executed. This call is then
    // a harmless no-op (window.seavienHideSplash → hide() is idempotent), and it remains the fallback
    // for any server-content route not (yet) wired to emit the instant-content signal.
    //
    // For routes whose container is empty or a text-less skeleton (home, deferred discussion), there is
    // nothing contentful to reveal, so we keep masking until after the mount (today's behavior) to
    // avoid flashing an empty page.
    const contentRevealedEarly = pageHasServerRenderedContent();
    if (contentRevealedEarly) {
        dismissFirstVisitSplash();
    }

    await mountAll();
    applyPostMountDocumentEffectsFromDocument(document, {
        flushAnalytics: false,
        trackPageContextEvent: false,
    });

    if (!contentRevealedEarly) {
        dismissFirstVisitSplash();
    }

    // Initialize the consent banner against the real (adopted) document so the per-request
    // `seavien-consent-banner-required` meta is present, including on edge-shell cold boots.
    initializeCookieConsent();

    // First-run welcome: open the onboarding carousel on the first authenticated page load for a
    // brand-new account, regardless of which screen rendered first. No-ops for anonymous/returning
    // users and once the once-ever flag is latched. Runs after mount so it layers over real content.
    maybeTriggerOnboardingWelcome();

    // In-product feedback prompt: arm the dwell timer. Deliberately AFTER the welcome trigger, since
    // it asks whether the welcome opened on this load and refuses to compete with it. No-ops for
    // anonymous visitors and for anyone the server has not marked due, and never opens immediately —
    // the whole point is to ask after the member has done what they came to do.
    maybeScheduleFeedbackPrompt();

    // SV-150 2b's age ask (`maybeStartAgeSignal`, react/age/AgeSignalRuntime.ts) is NOT mounted: the age asks are parked
    // until 100k members (r53 D4-D6). Re-mount it here, after the welcome trigger, when that trigger fires.
}

// Chrome islands (header, toaster) are present on every page, so their text never indicates that the
// *route* rendered real content. Only a non-chrome content container carrying real server-rendered
// text means it is safe to reveal early (Path A). Kept deliberately general so any future route that
// server-renders its above-the-fold content benefits without a per-route flag.
const CHROME_COMPONENT_NAMES = new Set(["MainHeader", "ToasterReactComponent"]);

function pageHasServerRenderedContent(): boolean {
    if (typeof document === "undefined") {
        return false;
    }
    const containers = document.querySelectorAll<HTMLElement>(".react-component[data-component]");
    for (const element of Array.from(containers)) {
        const componentName = element.getAttribute("data-component");
        if (componentName && CHROME_COMPONENT_NAMES.has(componentName)) {
            continue;
        }
        if ((element.textContent ?? "").trim().length > 0) {
            return true;
        }
    }
    return false;
}

// Dismiss the inline first-visit loading splash once React has mounted. The
// splash defines window.seavienHideSplash (with its own fail-open timeout), so
// this is the fast path and a no-op when no splash is present.
function dismissFirstVisitSplash(): void {
    if (typeof window === "undefined") {
        return;
    }
    const hideSplash = (window as Window & { seavienHideSplash?: () => void }).seavienHideSplash;
    if (typeof hideSplash !== "function") {
        return;
    }
    if (typeof requestAnimationFrame === "function") {
        // wait one frame so the mounted content has painted before we fade out
        requestAnimationFrame(() => hideSplash());
    } else {
        hideSplash();
    }
}

export function resetBootstrapStateForTests(): void {
    globalFrontEndSetupComplete = false;
    document.documentElement.removeAttribute(NATIVE_SHELL_DOCUMENT_ATTRIBUTE);
    resetCookieConsentForTests();
    resetAppAuthHandoffRuntimeForTests();
    resetNativeMobileAuthReturnForTests();
    // FI-2514 / FI-3157: only a chunk this module actually loaded has state to reset.
    loadedNativeUx?.resetMobileShellNativeUxForTests();
    loadedDeepLinkBridge?.resetNativeDeepLinksForTests();
    loadedResumeStateBridge?.resetNativeResumeStateForTests();
    loadedNativeUx = null;
    loadedDeepLinkBridge = null;
    loadedResumeStateBridge = null;
    nativeLifecycleBridgesScheduled = false;
    loadedShareTargetBridge?.resetNativeShareTargetForTests();
    loadedShareTargetBridge = null;
    resetMobilePushBridgeForTests();
    resetNativeAppIdentityForTests();
    installAttributionScheduled = false;
    resetAuthSessionSyncForTests();
    resetProactiveCsrfRefreshForTests();
    resetFirstTouchBeaconForTests();
    resetInferredContentLocaleCookieForTests();
    resetEnhancedNavigationForTests();
    resetNavigationPrefetchForTests();
    resetInPageAnchorScrollForTests();
    resetHeaderInboxForTests();
    resetNavigationStateForTests();
    resetThemeRuntimeForTests();
    resetPwaInstallRuntimeForTests();
    resetViewportRuntimeForTests();
    resetUnusablePageSignalsForTests();
    resetOnboardingWelcomeForTests();
    resetWebVitalsReportingForTests();
}
